import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import './ProjectGallery.css';

const ProjectGallery = ({ featuredImage, images, title }) => {
  const galleryItems = useMemo(() => {
    const items = [];

    if (featuredImage) {
      items.push({
        id: 'featured',
        src: featuredImage,
        caption: 'Imagine principală',
        alt: `${title} - imagine principală`,
      });
    }

    if (Array.isArray(images) && images.length > 0) {
      items.push(
        ...images.map((image, index) => ({
          id: image.id ?? `gallery-${index}`,
          src: image.image,
          caption: image.caption || `Galerie ${index + 1}`,
          alt: image.caption || `${title} - imagine ${index + 1}`,
        })),
      );
    }

    return items;
  }, [featuredImage, images, title]);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (!galleryItems.length) {
    return null;
  }

  const currentItem = galleryItems[activeIndex];
  const hasMultipleImages = galleryItems.length > 1;

  const selectImage = (index) => {
    setActiveIndex(index);
  };

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = () => setIsLightboxOpen(true);
  const closeLightbox = () => setIsLightboxOpen(false);

  return (
    <div className="project-gallery">
      <div
        className="project-gallery__main"
        role="button"
        tabIndex={0}
        onClick={openLightbox}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openLightbox();
          }
        }}
      >
        <img src={currentItem.src} alt={currentItem.alt} />
        <div className="project-gallery__overlay">
          <span>Vezi galeria</span>
        </div>
      </div>

      {hasMultipleImages && (
        <div className="project-gallery__thumbnails">
          {galleryItems.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={`project-gallery__thumbnail ${index === activeIndex ? 'is-active' : ''}`}
              onClick={() => selectImage(index)}
              aria-label={`Selectează imaginea ${index + 1}`}
            >
              <img src={item.src} alt={item.alt} />
            </button>
          ))}
        </div>
      )}

      {isLightboxOpen && (
        <div
          className="project-gallery__lightbox"
          role="presentation"
          onClick={closeLightbox}
        >
          <div
            className="project-gallery__lightbox-content"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="project-gallery__lightbox-close"
              onClick={closeLightbox}
              aria-label="Închide galeria"
            >
              ×
            </button>

            {hasMultipleImages && (
              <>
                <button
                  type="button"
                  className="project-gallery__lightbox-nav project-gallery__lightbox-nav--prev"
                  onClick={goToPrevious}
                  aria-label="Imaginea anterioară"
                >
                  ‹
                </button>

                <button
                  type="button"
                  className="project-gallery__lightbox-nav project-gallery__lightbox-nav--next"
                  onClick={goToNext}
                  aria-label="Imaginea următoare"
                >
                  ›
                </button>
              </>
            )}

            <div className="project-gallery__lightbox-image-wrapper">
              <img src={currentItem.src} alt={currentItem.alt} />
              {currentItem.caption && (
                <p className="project-gallery__lightbox-caption">{currentItem.caption}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

ProjectGallery.propTypes = {
  featuredImage: PropTypes.string,
  images: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image: PropTypes.string.isRequired,
      caption: PropTypes.string,
    }),
  ),
  title: PropTypes.string.isRequired,
};

ProjectGallery.defaultProps = {
  featuredImage: null,
  images: [],
};

export default ProjectGallery;



